import React, { useEffect, useRef, useState } from "react";
import { Send, Mic, Square, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import SuggestedActionChips from "./SuggestedActionChips";
import useChipMemory from "../hooks/useChipMemory";

const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

/**
 * V2.0.5 — Mobile chat-first dock.
 *
 * Goal: on a phone, the chat with the AI architect is the primary control
 * surface (not the SVG canvas). The dock sits at the bottom of the editor:
 *
 *   ┌─────────────────────────┐
 *   │      Floor Plan         │  ← canvas (top ~55-65% of viewport)
 *   ├─────────────────────────┤
 *   │ // architect (preview)  │  ← last reply line + expand toggle
 *   │ [chip] [chip] [chip]    │  ← SuggestedActionChips (tap to send)
 *   │ ┌─────────────────┐ 🎤  │  ← input + mic + send
 *   │ │ Describe…       │ ▶   │
 *   │ └─────────────────┘     │
 *   └─────────────────────────┘
 *
 * Tapping the expand chevron grows the dock to full-screen so the user can
 * scroll the full chat history. Tapping again collapses it back.
 */
export default function MobileChatDock({
  messages = [],
  onSend,
  loading,
  suggestedActions = [],
  onClearSuggestions,
}) {
  const [text, setText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const submitTimerRef = useRef(null);
  const scrollRef = useRef(null);
  // V2.0.6.1 — Personalised chip memory. The hook returns the user's
  // top-5 chips (cached in localStorage for instant first paint, then
  // refreshed from /api/me/chip-rankings on mount).
  const chipMemory = useChipMemory();
  // Track which chips are currently visible so we can count them as
  // "skips" if the user sends a free-form message instead of tapping one.
  const visibleChipsRef = useRef([]);

  // Auto-scroll the message history to the bottom whenever new messages arrive.
  useEffect(() => {
    if (expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, expanded]);

  const handleSend = (override, viaChip = null) => {
    const value = (override ?? text).trim();
    if (!value || loading) return;
    // V2.0.6.1 — Chip-memory accounting.
    //   - If the user TAPPED a chip, record the tap.
    //   - Otherwise, count every chip currently visible as a "skip" so
    //     unused chips decay out of the rotation over time.
    if (viaChip) {
      chipMemory.recordTap(viaChip);
    } else if (visibleChipsRef.current.length > 0) {
      chipMemory.recordSkip(visibleChipsRef.current);
    }
    onSend?.(value);
    setText("");
    onClearSuggestions?.();
  };

  // Tap-to-dictate. Uses the same Web Speech API as MobileMicFab — short
  // press starts recognition; auto-submits 5 s after silence; second tap
  // cancels. Kept self-contained so the dock works without MobileMicFab.
  const toggleMic = () => {
    if (!SpeechRecognitionCtor) return;
    if (listening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (err) { console.debug("[MobileChatDock] mic stop failed:", err); }
      setListening(false);
      return;
    }
    const rec = new SpeechRecognitionCtor();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";
    let final = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      setText((final + interim).trim());
      clearTimeout(submitTimerRef.current);
      submitTimerRef.current = setTimeout(() => {
        try { rec.stop(); } catch (err) { console.debug("[MobileChatDock] auto-stop failed:", err); }
      }, 5000);
    };
    rec.onend = () => {
      setListening(false);
      const value = (final || text).trim();
      if (value) handleSend(value);
    };
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try { rec.start(); } catch (_) { setListening(false); }
  };

  // Pull the most recent assistant message (if any) to show as a 1-line
  // preview in the collapsed state. We display the user's last prompt
  // beneath it when present so the user has context without expanding.
  const lastAssistant = [...messages].reverse().find((m) => m.role === "architect");
  const previewText = lastAssistant?.content || lastAssistant?.text || "// Ready when you are.";

  return (
    <div
      data-testid="mobile-chat-dock"
      className={
        "lg:hidden fixed left-0 right-0 bottom-0 z-30 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-800 shadow-[0_-12px_36px_rgba(0,0,0,0.6)] " +
        (expanded ? "top-[120px]" : "")
      }
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Header row — expand/collapse + last-reply preview */}
      <button
        type="button"
        data-testid="mobile-chat-dock-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 border-b border-zinc-800 hover:bg-zinc-900/60 transition-colors"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-4 h-4 text-brass-400 flex-shrink-0" />
        )}
        <span className="font-mono text-[9px] uppercase tracking-widest text-brass-400 flex-shrink-0">
          // architect
        </span>
        <span className="text-zinc-300 text-[12px] truncate flex-1 text-left">
          {previewText}
        </span>
      </button>

      {/* Expanded history view — scrolls the full chat log. */}
      {expanded && (
        <div
          ref={scrollRef}
          data-testid="mobile-chat-dock-history"
          className="overflow-y-auto px-3 py-2 space-y-3"
          style={{ maxHeight: "calc(100vh - 260px)" }}
        >
          {messages.map((m, i) => {
            // V2.0.72 — mirror ChatPane null-guard.
            if (!m || !m.role) return null;
            return (
            <div
              key={m.id || `${m.role}-${i}-${(m.content || m.text || "").slice(0, 24)}`}
              className={m.role === "user" ? "text-right" : "text-left"}
            >
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">
                // {m.role === "user" ? "you" : "architect"}
              </div>
              <div
                className={
                  "inline-block max-w-[88%] text-[13px] leading-relaxed px-3 py-2 border " +
                  (m.role === "user"
                    ? "bg-zinc-900 border-zinc-800 text-zinc-100"
                    : "bg-brass-950/30 border-brass-900/50 text-zinc-100")
                }
              >
                {m.content || m.text}
              </div>
            </div>
            );
          })}
          {loading && (
            <div className="flex items-center gap-2 text-brass-400 font-mono text-[10px] uppercase tracking-widest">
              <Loader2 className="w-3 h-3 animate-spin" /> Architect is drafting…
            </div>
          )}
        </div>
      )}

      {/* V2.0.6 — Suggested action chips, ALWAYS rendered on mobile.
          When the AI emits a <SUGGEST> block via the system prompt the
          chips reflect the latest reply; when it doesn't, we fall back
          to the user's PERSONALISED brainstorm set (V2.0.6.1 chip
          memory). Tap = send the chip text verbatim AND record a tap
          event so the rotation adapts to the user's taste over time. */}
      <div className="px-3 pt-2">
        <SuggestedActionChips
          actions={suggestedActions}
          loading={loading}
          showFallback
          personalisedFallback={chipMemory.chips}
          onVisibleChange={(list) => { visibleChipsRef.current = list; }}
          onPick={(prompt) => handleSend(prompt, prompt)}
        />
      </div>

      {/* Input row: text + mic + send. Min 44 px tap targets per Apple HIG. */}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
        className="flex items-center gap-2 px-3 py-2"
      >
        <input
          type="text"
          data-testid="mobile-chat-dock-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Describe the change… e.g. add a sliding glass door"
          disabled={loading}
          className="flex-1 min-h-[44px] px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 placeholder-zinc-500 text-[14px] focus:border-brass-500 focus:outline-none"
        />
        {SpeechRecognitionCtor && (
          <button
            type="button"
            data-testid="mobile-chat-dock-mic"
            onClick={toggleMic}
            disabled={loading}
            aria-pressed={listening}
            aria-label={listening ? "Stop dictation" : "Start dictation"}
            className={
              "min-w-[44px] min-h-[44px] grid place-items-center border transition-colors " +
              (listening
                ? "bg-orange-600 border-orange-400 text-white"
                : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-brass-500 hover:text-brass-300")
            }
          >
            {listening ? <Square className="w-4 h-4 fill-current" /> : <Mic className="w-5 h-5" />}
          </button>
        )}
        <button
          type="submit"
          data-testid="mobile-chat-dock-send"
          disabled={loading || !text.trim()}
          aria-label="Send"
          className={
            "min-w-[44px] min-h-[44px] grid place-items-center border transition-colors " +
            (loading || !text.trim()
              ? "bg-zinc-900 border-zinc-800 text-zinc-600"
              : "bg-brass-500 border-brass-300 text-zinc-950 hover:bg-brass-400 active:scale-95")
          }
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
}
